import { router, useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BackChevron } from '@/components/BackChevron';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { HeroEarningsCard } from '@/components/HeroEarningsCard';
import { HowItWorksFooter } from '@/components/HowItWorksFooter';
import { QrSheet } from '@/components/QrSheet';
import { ReferralDetailSheet } from '@/components/ReferralDetailSheet';
import { ShareMenu } from '@/components/ShareMenu';
import { Toast } from '@/components/Toast';
import {
  WhoClickedCard,
  DEFAULT_FILTERS,
  type WhoClickedFilters,
  type WhoClickedTextableStage,
} from '@/components/WhoClickedCard';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { openEmail } from '@/lib/openEmail';
import { authStore } from '@/stores/auth.store';
import { dealsStore } from '@/stores/deals.store';
import { everyoneFeedStore } from '@/stores/everyoneFeed.store';
import { referralLinksStore } from '@/stores/referralLinks.store';
import { referralStatsStore } from '@/stores/referralStats.store';
import { referralsStore } from '@/stores/referrals.store';
import type { EveryoneFeedItem } from '@/types/referral';

/**
 * Per-deal referral detail — the tap-in target from the Refer tab index
 * (2026-08-24 restructure). Carries what used to be the whole Refer tab
 * minus the deal-picker pills: the deal now comes from the route.
 *
 *   1. Hero earnings card — "YOU'VE EARNED $X" + Share my link
 *   2. Friends card — Started · Paid · Not started tabs, each with its
 *      panel ("$100 waiting on 5" + Text all) and per-friend progress rows
 *      (mock P1e, 2026-09-13; replaced the cream callout + chip row)
 *   3. How-it-works footer
 *
 * The feed IS deal-scoped: `dealId` goes to the server, so a SoFi header
 * never sits above someone who only ever clicked a Fetch link. The
 * headline stats above it stay account-level for now.
 *
 * Rows open a detail sheet (Nudge / Remove from list). Removal is a
 * soft hide — see BUSINESS_RULES.md "Removing a referral".
 */

/**
 * How long a removed row stays on screen as an undoable tombstone. Long
 * enough to notice and reverse a mis-tap, short enough that the list
 * settles on its own.
 */
const UNDO_WINDOW_MS = 8000;

const DealReferralScreen = observer(() => {
  const { id } = useLocalSearchParams<{ id: string }>();
  // A positive integer or 0 — never NaN into a request or a cache key. 0
  // matches no deal, so junk resolves to the not-shareable state below.
  const parsedId = Number.parseInt(id ?? '', 10);
  const dealId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : 0;

  const [filters, setFilters] = useState<WhoClickedFilters>(DEFAULT_FILTERS);
  const [detailItem, setDetailItem] = useState<EveryoneFeedItem | null>(null);
  /**
   * True when the sheet was opened by swiping a row, so it lands directly on
   * "Remove X from your list?". Swipe and the sheet share one confirmation
   * surface rather than each growing their own.
   */
  const [detailConfirming, setDetailConfirming] = useState(false);
  /** Pending settle timers, keyed by referral id, so Undo can cancel one. */
  const undoTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const [shareOpen, setShareOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    void dealsStore.ensureLoaded();
    void referralsStore.ensureLoaded();
    void referralLinksStore.ensureLoaded();
    if (dealId) void referralStatsStore.ensureDealLoaded(dealId);
  }, [dealId]);

  // Server filters by tab + search and orders within the tab
  // (`sort=last_update`: Started stalest-first, Paid newest, Not started
  // newest signup). Nothing is sorted or grouped on the client.
  // Debounce the search by ~250ms so we don't spam the API on every keystroke.
  useEffect(() => {
    if (!dealId) return;
    const handle = setTimeout(() => {
      void everyoneFeedStore.fetch({
        dealId,
        stage: filters.stage,
        search: filters.search ?? null,
        sort: 'last_update',
      });
    }, filters.search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [dealId, filters.stage, filters.search]);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 2500);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  // Settle timers are view state, not store state — drop them on unmount
  // so a backgrounded screen doesn't fire a stats refresh into a dead screen.
  useEffect(() => {
    const timers = undoTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const onRefresh = useCallback(() => {
    // The deal's own copy (FAQ, steps, nudge template) lives on the deal
    // record; a pull must refetch it too, like the index does. The PULL
    // variant: `refreshList` is the throttled focus refresher and no-ops
    // inside its 30s window, which is exactly when someone pulls.
    void dealsStore.pullRefreshList();
    void referralsStore.refresh().catch(() => {});
    void referralLinksStore.refresh().catch(() => {});
    void referralStatsStore.refreshDeal(dealId);
    void everyoneFeedStore.fetch({
      dealId,
      stage: filters.stage,
      search: filters.search ?? null,
      sort: 'last_update',
    });
  }, [dealId, filters.stage, filters.search]);

  const onShare = useCallback(() => {
    setShareOpen(true);
  }, []);

  // The nudge text names a product and a dollar amount, so it has to know
  // WHICH deal is being nudged — an Invest friend must not get Checking's
  // copy and number. This screen is deal-scoped; pass it through and the
  // nudge screen scopes its own feed fetch to it.
  // "Text all" on a tab panel: the nudge flow lists that tab's friends
  // (Started: they opened SoFi and stalled; Not started: they never did)
  // with Select-all prominent.
  const onTextAll = useCallback((stage: WhoClickedTextableStage) => {
    router.push({ pathname: '/referrals/nudge', params: { deal_id: String(dealId), stage } });
  }, [dealId]);

  const onRowNudge = useCallback((item: EveryoneFeedItem) => {
    router.push({
      pathname: '/referrals/nudge',
      params: { preselect_referral_id: String(item.referral_id), deal_id: String(dealId) },
    });
  }, [dealId]);

  const onOpenDetail = useCallback((item: EveryoneFeedItem) => {
    setDetailConfirming(false);
    setDetailItem(item);
  }, []);

  // Swipe asks, it doesn't remove. The gesture is deliberate but easy to
  // fire by accident while scrolling, and removal is the one action here
  // that a user can't undo by simply looking away.
  const onRequestRemove = useCallback((item: EveryoneFeedItem) => {
    setDetailConfirming(true);
    setDetailItem(item);
  }, []);

  const onDetailNudge = useCallback(
    (item: EveryoneFeedItem) => {
      setDetailItem(null);
      onRowNudge(item);
    },
    [onRowNudge],
  );

  /**
   * Remove a dead lead. Optimistic — the row becomes a tombstone
   * immediately and the undo window opens. Two things are deliberately
   * deferred until that window closes:
   *
   *   1. Dropping the row for real (`settleRemoval`).
   *   2. Refreshing referralStatsStore.
   *
   * (2) matters more than it looks: the friends card's tab panel prints
   * the stats' numbers and sits ABOVE the rows, so refetching stats
   * immediately can rewrite the panel and shift the list under the user's
   * finger while they're deciding whether to undo.
   */
  const onRemove = useCallback(async (item: EveryoneFeedItem) => {
    setDetailItem(null);
    const ok = await everyoneFeedStore.remove(item.referral_id, dealId);
    if (!ok) {
      setToastMessage(`Couldn't remove ${item.first_name || item.name}`);
      return;
    }

    AccessibilityInfo.announceForAccessibility(
      `${item.first_name || item.name} removed. Undo available.`,
    );

    // Replace, never stack. A double-tapped confirm would otherwise leave the
    // first timer orphaned in the closure — Undo clears only the Map entry, so
    // the orphan survives and can settle a later removal of the same row early.
    const existing = undoTimers.current.get(item.referral_id);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      everyoneFeedStore.settleRemoval(item.referral_id);
      undoTimers.current.delete(item.referral_id);
      void referralStatsStore.refreshDeal(dealId);
    }, UNDO_WINDOW_MS);
    undoTimers.current.set(item.referral_id, timer);
  }, [dealId]);

  const onUndoRemove = useCallback(
    async (item: EveryoneFeedItem) => {
      const timer = undoTimers.current.get(item.referral_id);
      if (timer) {
        clearTimeout(timer);
        undoTimers.current.delete(item.referral_id);
      }
      const ok = await everyoneFeedStore.undoRemoval(item.referral_id, dealId);
      if (!ok) {
        setToastMessage(`Couldn't undo — pull to refresh`);
        return;
      }
      AccessibilityInfo.announceForAccessibility(
        `${item.first_name || item.name} back on your list.`,
      );
      // Refetch, don't just drop the tombstone. Undoing from the "Removed"
      // chip otherwise leaves the row rendering dimmed with its UNDO pill —
      // `item.hidden` came from the server payload and nothing local flips
      // it — while chip_counts already says it's back. Counts and list would
      // disagree, which is the drift the server-side partition exists to
      // prevent. Server stays the source of truth (non-negotiable #6).
      void everyoneFeedStore.fetch({
        dealId,
        stage: filters.stage,
        search: filters.search ?? null,
        sort: 'last_update',
      });
      void referralStatsStore.refreshDeal(dealId);
    },
    [dealId, filters.stage, filters.search],
  );

  const onHelpCenter = useCallback(async () => {
    const result = await openEmail('jason@marketpryce.com', { silent: true });
    if (result === 'copied') setToastMessage('jason@marketpryce.com copied');
    else if (result === 'unavailable') setToastMessage('Email jason@marketpryce.com');
  }, []);

  const deal = dealId ? dealsStore.deals.find((d) => d.id === dealId) ?? null : null;

  // No deal to draw: still loading (cold-start deep link), the list failed
  // to load, or the deal is gone / not shareable (deactivated between index
  // render and tap, or a stale link). Never a placeholder dollar figure —
  // every number on this screen is the deal's own.
  if (!deal || !deal.referrals_enabled) {
    return (
      <SafeAreaView style={styles.safe} edges={['top']}>
        <View style={styles.header}>
          <BackChevron />
        </View>
        <View style={styles.emptyContainer}>
          {dealsStore.hasLoaded ? (
            <EmptyState
              title="This deal isn't shareable right now"
              body="Head back to the Refer tab to see what you can share."
              cta={{ label: 'Back to Refer', onPress: () => router.back() }}
            />
          ) : dealsStore.error ? (
            <ErrorState
              title="Couldn't load this deal"
              body="Check your connection and try again."
              onRetry={() => void dealsStore.ensureLoaded()}
              testID="deal-referral-error"
            />
          ) : (
            <ActivityIndicator color={colors.deepGreen} testID="deal-referral-loading" />
          )}
        </View>
      </SafeAreaView>
    );
  }

  // This screen is one deal's dashboard: the hero prints what THIS deal has
  // paid the ambassador (server-summed per deal), not the all-programs total
  // the index shows. A Checking ambassador opening Invest starts at $0 here.
  const totalEarnedCents = referralsStore.paidEarningsCentsForDeal(dealId);
  // The tab panels' numbers. Null until the deal's stats land (or if they
  // failed): the card then prints only what the feed's own counts can
  // vouch for, and the pull refreshes. No separate error block: the
  // panels are not this screen's headline.
  const dealStats = referralStatsStore.forDeal(dealId);
  const friendStats = dealStats
    ? {
      inProgressCount: dealStats.totals.friends_in_progress_count,
      leftOnTableCents: dealStats.totals.left_on_table_cents,
      earnedCents: totalEarnedCents,
    }
    : null;
  const perReferralDollars = Math.floor(deal.referrer_cut_cents / 100);

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={referralsStore.refreshing}
            onRefresh={onRefresh}
            tintColor={colors.deepGreen}
          />
        }
      >
        <View style={styles.header}>
          <BackChevron />
          <Text style={styles.headerTitle}>{deal ? deal.display_name : ''}</Text>
        </View>

        {authStore.user ? (
          <HeroEarningsCard
            earnedCents={totalEarnedCents}
            offerName={deal.display_name}
            perReferralDollars={perReferralDollars}
            onShare={onShare}
            deal={deal}
          />
        ) : null}

        <WhoClickedCard
          items={everyoneFeedStore.items}
          chipCounts={everyoneFeedStore.chipCounts}
          filters={filters}
          onFiltersChange={setFilters}
          stats={friendStats}
          programName={deal.display_name}
          onTextAll={onTextAll}
          onNudge={onRowNudge}
          onOpenDetail={onOpenDetail}
          onUndoRemove={(item) => void onUndoRemove(item)}
          onRequestRemove={onRequestRemove}
          tombstonedIds={everyoneFeedStore.tombstoned}
          loading={everyoneFeedStore.refreshing && !everyoneFeedStore.loaded}
          error={everyoneFeedStore.loaded ? null : everyoneFeedStore.refreshError}
          onRetry={() =>
            void everyoneFeedStore.fetch({
              dealId,
              stage: filters.stage,
              search: filters.search ?? null,
              sort: 'last_update',
            })
          }
        />

        <HowItWorksFooter
          deal={deal}
          perReferralDollars={perReferralDollars}
          onContactSupport={onHelpCenter}
        />
      </ScrollView>

      <ShareMenu
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        selectedDeal={deal}
        onShowQr={async () => {
          if (!deal) return;
          setShareOpen(false);
          // Mint-or-find the deal's QR-TAGGED link (source='qr') and encode
          // ITS url, so scans attribute as link_source=qr — the Copy row's
          // plain link would be invisible to QR-conditioned bonuses.
          try {
            const link = await referralLinksStore.ensureQrLinkForDeal(deal.id);
            setQrUrl(link.public_url);
            setQrOpen(true);
          } catch {
            // referralLinksStore.createError surfaces this; toast the user.
            setToastMessage('Could not generate share link');
          }
        }}
        onCopied={(channelLabel) =>
          setToastMessage(channelLabel ? `${channelLabel} link copied` : 'Link copied')
        }
      />
      <ReferralDetailSheet
        isOpen={detailItem !== null}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        onNudge={onDetailNudge}
        onRemove={(item) => void onRemove(item)}
        startConfirming={detailConfirming}
        nudgeTemplate={deal.marketing_copy?.nudge_template ?? null}
        testID="referral-detail-sheet"
      />
      <QrSheet
        isOpen={qrOpen}
        onClose={() => {
          setQrOpen(false);
          setQrUrl(null);
        }}
        url={qrUrl}
        caption={deal ? `${deal.display_name} link` : 'Share link'}
      />

      <Toast message={toastMessage} />
    </SafeAreaView>
  );
});

export default DealReferralScreen;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: spacing['3xl'],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerTitle: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.lg,
    color: colors.deepGreen,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
});
